import React from 'react';
import { BoltIcon, CpuChipIcon, PowerIcon } from '@heroicons/react/24/outline';

interface SystemOverviewData {
  dc_capacity_mwp: number;
  ac_capacity_mw: number;
  generation_kwh: number;
  active_power_kw: number;
  active_power_ac_mw: number;
}

interface SystemOverviewPanelProps {
  data: SystemOverviewData;
  loading?: boolean;
}

const SystemOverviewPanel: React.FC<SystemOverviewPanelProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const metrics = [
    {
      label: 'DC Capacity',
      value: `${data.dc_capacity_mwp.toFixed(2)} MWp`,
      icon: CpuChipIcon,
      color: 'text-blue-600'
    },
    {
      label: 'AC Capacity',
      value: `${data.ac_capacity_mw.toFixed(2)} MW`,
      icon: PowerIcon,
      color: 'text-green-600'
    },
    {
      label: 'Generation',
      value: `${data.generation_kwh.toFixed(2)} kWh`,
      icon: BoltIcon,
      color: 'text-yellow-600'
    },
    {
      label: 'Active Power',
      value: `${data.active_power_kw.toFixed(2)} kW`,
      icon: PowerIcon,
      color: 'text-purple-600'
    },
    {
      label: 'Active Power (AC)',
      value: `${data.active_power_ac_mw.toFixed(2)} MW`,
      icon: BoltIcon,
      color: 'text-orange-600'
    }
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border">
      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
        Capacity & Asset Status
      </h3>
      <div className="space-y-4">
        {metrics.map((metric, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center gap-3">
              <metric.icon className={`w-5 h-5 ${metric.color}`} />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {metric.label}
              </span>
            </div>
            <span className="text-lg font-bold text-gray-900 dark:text-white">
              {metric.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SystemOverviewPanel;